import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { apiUrl } from "../helper";
import { STAND_OPTIONS, STANDS } from "../stands";
import {
    disableAdminPush,
    enableAdminPush,
    getPushPermission,
    pushSupported,
    registerAdminServiceWorker,
    sendTestPush,
} from "../pushNotifications";
import { createAlertRingtone } from "../alertRingtone";
import "../styles/AdminPanel.scss";

const emptyArticleForm = {
    categoryId: "",
    name: "",
    image: "",
    price: "",
    stock: "",
    active: true,
};

const VIEWS = {
    CATALOG: "catalog",
    FORM: "form",
    CATEGORIES: "categories",
    STATS: "stats",
    CAISSES: "caisses",
};

const AdminPanel = () => {
    const [token, setToken] = useState(localStorage.getItem("adminToken") || "");
    const [pin, setPin] = useState("");
    const [categories, setCategories] = useState([]);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [categoryName, setCategoryName] = useState("");
    const [categoryStand, setCategoryStand] = useState("repas");
    const [articleForm, setArticleForm] = useState(emptyArticleForm);
    const [editingArticleId, setEditingArticleId] = useState(null);
    const [mobileView, setMobileView] = useState(VIEWS.CATALOG);
    const [desktopView, setDesktopView] = useState(VIEWS.CATALOG);
    const [formOpen, setFormOpen] = useState(false);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [statsScope, setStatsScope] = useState("all");
    const [caisses, setCaisses] = useState([]);
    const [caissesLoading, setCaissesLoading] = useState(false);
    const [syncConflicts, setSyncConflicts] = useState([]);
    const [recentInvoices, setRecentInvoices] = useState([]);
    const [reprintingId, setReprintingId] = useState(null);
    const [pushEnabled, setPushEnabled] = useState(localStorage.getItem("adminPushEnabled") === "1");
    const [pushBusy, setPushBusy] = useState(false);
    const [pushHint, setPushHint] = useState("");
    const [incomingAlert, setIncomingAlert] = useState(null);
    const [ackBusy, setAckBusy] = useState(false);
    const ringtoneRef = useRef(null);
    const incomingAlertRef = useRef(null);

    const authHeaders = { "x-admin-token": token };

    const stopRingtone = useCallback(() => {
        if (ringtoneRef.current) {
            ringtoneRef.current.stop();
            ringtoneRef.current = null;
        }
    }, []);

    const startRingtone = useCallback(() => {
        stopRingtone();
        const ring = createAlertRingtone();
        ringtoneRef.current = ring;
        ring.start();
    }, [stopRingtone]);

    const openIncomingAlert = useCallback((alert) => {
        if (!alert || alert.status === "acked") return;
        incomingAlertRef.current = alert;
        setIncomingAlert(alert);
        startRingtone();
    }, [startRingtone]);

    const fetchAndOpenAlert = useCallback(async (alertId) => {
        if (!token || !alertId) return;
        try {
            const response = await axios.get(`${apiUrl}/api/admin/alerts/${alertId}`, {
                headers: { "x-admin-token": token },
            });
            openIncomingAlert(response.data.alert);
        } catch (_error) {
            // ignore
        }
    }, [token, openIncomingAlert]);

    const ackIncomingAlert = async () => {
        if (!incomingAlert?.id || ackBusy) return;
        setAckBusy(true);
        try {
            await axios.post(
                `${apiUrl}/api/admin/alerts/${incomingAlert.id}/ack`,
                {},
                { headers: authHeaders },
            );
            stopRingtone();
            incomingAlertRef.current = null;
            setIncomingAlert(null);
            const url = new URL(window.location.href);
            if (url.searchParams.has("alert")) {
                url.searchParams.delete("alert");
                window.history.replaceState({}, "", url.pathname + url.search);
            }
            setMessage("Alerte validée");
        } catch (error) {
            setMessage(error.response?.data?.message || "Validation impossible");
        } finally {
            setAckBusy(false);
        }
    };

    const loadCatalog = useCallback(async () => {
        setLoading(true);
        try {
            const response = await axios.get(`${apiUrl}/api/admin/catalog`, { headers: authHeaders });
            setCategories(response.data.categories);
            setSelectedCategoryId((current) => current || response.data.categories[0]?.id || null);
            setMessage("");
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem("adminToken");
                setToken("");
            }
            setMessage("Session expirée ou erreur de chargement");
        } finally {
            setLoading(false);
        }
    }, [token]);

    const loadStats = useCallback(async () => {
        setStatsLoading(true);
        try {
            const today = new Date().toISOString().slice(0, 10);
            const url = statsScope === "today"
                ? `${apiUrl}/api/admin/stats?date=${today}`
                : `${apiUrl}/api/admin/stats`;
            const response = await axios.get(url, { headers: authHeaders });
            setStats(response.data.stats);
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem("adminToken");
                setToken("");
            }
        } finally {
            setStatsLoading(false);
        }
    }, [token, statsScope]);

    const loadCaisses = useCallback(async () => {
        setCaissesLoading(true);
        try {
            const [caissesRes, conflictsRes, invoicesRes] = await Promise.all([
                axios.get(`${apiUrl}/api/admin/caisses`, { headers: authHeaders }),
                axios.get(`${apiUrl}/api/admin/sync-conflicts`, { headers: authHeaders }).catch(() => null),
                axios.get(`${apiUrl}/api/admin/invoices/recent`, { headers: authHeaders }).catch(() => null),
            ]);
            setCaisses(caissesRes.data.caisses || []);
            setSyncConflicts(conflictsRes?.data?.conflicts || []);
            setRecentInvoices(invoicesRes?.data?.invoices || []);
        } catch (error) {
            if (error.response?.status === 401) {
                localStorage.removeItem("adminToken");
                setToken("");
            }
        } finally {
            setCaissesLoading(false);
        }
    }, [token]);

    const reprintOnCaisse = async (invoice) => {
        if (!invoice?.id) return;
        setReprintingId(invoice.id);
        setMessage("");
        try {
            const response = await axios.post(
                `${apiUrl}/api/admin/invoices/${invoice.id}/reprint`,
                { code: invoice.code || undefined },
                { headers: authHeaders },
            );
            setMessage(response.data.message || "Impression relancée sur la caisse");
        } catch (error) {
            setMessage(error.response?.data?.message || "Impossible de réimprimer sur la caisse");
        } finally {
            setReprintingId(null);
        }
    };

    const caisseLabelForCode = (code) => {
        if (!code) return "—";
        const match = caisses.find((c) => c.code === code);
        return match ? `${match.name} (${code})` : `Caisse ${code}`;
    };

    useEffect(() => {
        if (token) loadCatalog();
    }, [token, loadCatalog]);

    useEffect(() => {
        if (!token) return;
        if (mobileView === VIEWS.STATS || desktopView === VIEWS.STATS) {
            loadStats();
        }
    }, [token, mobileView, desktopView, statsScope, loadStats]);

    useEffect(() => {
        if (!token) return;
        if (mobileView !== VIEWS.CAISSES && desktopView !== VIEWS.CAISSES) return undefined;

        loadCaisses();
        const timer = setInterval(loadCaisses, 10000);
        return () => clearInterval(timer);
    }, [token, mobileView, desktopView, loadCaisses]);

    const login = async (event) => {
        event.preventDefault();
        try {
            const response = await axios.post(`${apiUrl}/api/admin/login`, { pin });
            localStorage.setItem("adminToken", response.data.token);
            setToken(response.data.token);
            setMessage("");
        } catch (error) {
            setMessage(error.response?.data?.message || "Connexion impossible");
        }
    };

    const logout = () => {
        localStorage.removeItem("adminToken");
        setToken("");
    };

    const togglePush = async () => {
        if (!pushSupported()) {
            setPushHint("Notifications non supportées (installe l’app sur l’écran d’accueil iOS/Android).");
            return;
        }
        setPushBusy(true);
        setPushHint("");
        try {
            if (pushEnabled) {
                await disableAdminPush(token);
                setPushEnabled(false);
                setPushHint("Notifications désactivées");
            } else {
                await enableAdminPush(token);
                setPushEnabled(true);
                setPushHint("Notifications activées — tu recevras stock bas, caisses hors ligne, etc.");
                try {
                    await sendTestPush(token);
                } catch (_e) {
                    // ignore test failure
                }
            }
        } catch (error) {
            setPushHint(error.message || "Impossible d’activer les notifications");
        } finally {
            setPushBusy(false);
        }
    };

    useEffect(() => {
        if (!token || !pushSupported()) return undefined;
        registerAdminServiceWorker().catch(() => {});
        setPushEnabled(localStorage.getItem("adminPushEnabled") === "1" && getPushPermission() === "granted");
        return undefined;
    }, [token]);

    // Ouvre l'alerte depuis ?alert=ID (clic notif) + écoute SW + poll pending
    useEffect(() => {
        if (!token) return undefined;

        const params = new URLSearchParams(window.location.search);
        const fromUrl = params.get("alert");
        if (fromUrl) {
            fetchAndOpenAlert(fromUrl);
        }

        const onMessage = (event) => {
            if (event.data?.type !== "CAISSE_ALERT") return;
            if (event.data.alertId) {
                fetchAndOpenAlert(event.data.alertId);
            }
        };
        if (navigator.serviceWorker) {
            navigator.serviceWorker.addEventListener("message", onMessage);
        }

        const poll = async () => {
            try {
                const response = await axios.get(`${apiUrl}/api/admin/alerts/pending`, {
                    headers: { "x-admin-token": token },
                });
                const pending = response.data.alerts || [];
                if (pending.length > 0 && !incomingAlertRef.current) {
                    openIncomingAlert(pending[0]);
                }
            } catch (_error) {
                // ignore
            }
        };
        poll();
        const interval = window.setInterval(poll, 8000);

        return () => {
            window.clearInterval(interval);
            if (navigator.serviceWorker) {
                navigator.serviceWorker.removeEventListener("message", onMessage);
            }
        };
    }, [token, fetchAndOpenAlert, openIncomingAlert]);

    useEffect(() => () => stopRingtone(), [stopRingtone]);

    const addCategory = async () => {
        if (!categoryName.trim()) return;
        await axios.post(`${apiUrl}/api/admin/categories`, {
            name: categoryName,
            stand: categoryStand,
        }, { headers: authHeaders });
        setCategoryName("");
        setCategoryStand("repas");
        loadCatalog();
    };

    const updateCategoryStand = async (category, stand) => {
        await axios.put(`${apiUrl}/api/admin/categories/${category.id}`, {
            stand,
        }, { headers: authHeaders });
        loadCatalog();
    };

    const toggleCategory = async (category) => {
        await axios.put(`${apiUrl}/api/admin/categories/${category.id}`, {
            active: !category.active,
        }, { headers: authHeaders });
        loadCatalog();
    };

    const resetForm = () => {
        setArticleForm(emptyArticleForm);
        setEditingArticleId(null);
        setFormOpen(false);
    };

    const saveArticle = async (event) => {
        event.preventDefault();
        const payload = {
            categoryId: Number(articleForm.categoryId || selectedCategoryId),
            name: articleForm.name,
            image: articleForm.image,
            price: Number(articleForm.price),
            stock: articleForm.stock === "" ? null : Number(articleForm.stock),
            active: articleForm.active,
        };

        if (editingArticleId) {
            await axios.put(`${apiUrl}/api/admin/articles/${editingArticleId}`, payload, { headers: authHeaders });
        } else {
            await axios.post(`${apiUrl}/api/admin/articles`, payload, { headers: authHeaders });
        }

        resetForm();
        setMobileView(VIEWS.CATALOG);
        loadCatalog();
    };

    const isDesktop = () => typeof window !== "undefined" && window.innerWidth >= 900;

    const openEdit = (article) => {
        setEditingArticleId(article.id);
        setArticleForm({
            categoryId: String(article.categoryId || selectedCategoryId),
            name: article.name,
            image: article.image,
            price: String(article.price),
            stock: article.stock === null ? "" : String(article.stock),
            active: article.active,
        });
        if (isDesktop()) {
            setFormOpen(true);
        } else {
            setMobileView(VIEWS.FORM);
        }
    };

    const openNew = () => {
        setEditingArticleId(null);
        setArticleForm({ ...emptyArticleForm, categoryId: String(selectedCategoryId || "") });
        if (isDesktop()) {
            setFormOpen(true);
        } else {
            setMobileView(VIEWS.FORM);
        }
    };

    const toggleArticle = async (article) => {
        await axios.put(`${apiUrl}/api/admin/articles/${article.id}`, {
            active: !article.active,
        }, { headers: authHeaders });
        loadCatalog();
    };

    const deleteArticle = async (articleId) => {
        if (!window.confirm("Supprimer cet article ?")) return;
        await axios.delete(`${apiUrl}/api/admin/articles/${articleId}`, { headers: authHeaders });
        loadCatalog();
    };

    const selectedCategory = categories.find((c) => c.id === selectedCategoryId);
    const totalArticles = categories.reduce((n, c) => n + c.articles.length, 0);

    const formatTime = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleString("fr-FR", {
            hour: "2-digit",
            minute: "2-digit",
            day: "2-digit",
            month: "2-digit",
        });
    };

    const formatAgo = (ms) => {
        if (ms == null) return "—";
        const sec = Math.round(ms / 1000);
        if (sec < 60) return `il y a ${sec}s`;
        const min = Math.round(sec / 60);
        if (min < 60) return `il y a ${min} min`;
        const h = Math.round(min / 60);
        return `il y a ${h} h`;
    };

    const onlineCount = caisses.filter((c) => c.online).length;

    const caissesPanel = (
        <div className="caisses-panel anim-fade">
            <div className="caisses-panel__head">
                <div>
                    <h2>Surveillance des caisses</h2>
                    <p className="caisses-panel__sub">
                        {onlineCount} en ligne · {caisses.length} enregistrée{caisses.length > 1 ? "s" : ""}
                        {" · "}Active les notifs pour être alerté hors ligne / stock bas
                    </p>
                </div>
                <button type="button" className="btn-ghost btn-sm" onClick={loadCaisses} disabled={caissesLoading}>
                    Actualiser
                </button>
            </div>

            {caissesLoading && caisses.length === 0 ? (
                <div className="skeleton-list">
                    {[1, 2, 3].map((n) => <div key={n} className="skeleton-row anim-shimmer" />)}
                </div>
            ) : caisses.length === 0 ? (
                <div className="caisses-empty">
                    <p>Aucune caisse connectée pour le moment.</p>
                    <p>Téléchargez l&apos;agent depuis la page d&apos;accueil et installez-le sur chaque PC caisse.</p>
                    <a className="btn-primary" href="/downloads/CaisseFoireSetup.exe" download>
                        Télécharger l&apos;installateur
                    </a>
                </div>
            ) : (
                <div className="caisses-grid">
                    {caisses.map((caisse) => (
                        <article key={caisse.id} className={`caisse-card ${caisse.online ? "is-online" : "is-offline"}`}>
                            <div className="caisse-card__status">
                                <span className="caisse-card__dot" />
                                {caisse.online ? "En ligne" : "Hors ligne"}
                            </div>
                            <h3>{caisse.name}</h3>
                            <dl className="caisse-card__meta">
                                <div>
                                    <dt>Code</dt>
                                    <dd>{caisse.code || "—"}</dd>
                                </div>
                                <div>
                                    <dt>Imprimante</dt>
                                    <dd>{caisse.printer || "—"}</dd>
                                </div>
                                <div>
                                    <dt>Ecrans</dt>
                                    <dd>{caisse.screens != null ? caisse.screens : "—"}</dd>
                                </div>
                                <div>
                                    <dt>Signal</dt>
                                    <dd>{formatAgo(caisse.lastSeenAgoMs)}</dd>
                                </div>
                            </dl>
                        </article>
                    ))}
                </div>
            )}

            {syncConflicts.length > 0 && (
                <div className="sync-conflicts" style={{ marginTop: 24 }}>
                    <h3>Conflits de stock (sync offline)</h3>
                    <p className="caisses-panel__sub">
                        Tickets acceptés alors que le stock serveur était insuffisant.
                    </p>
                    <ul>
                        {syncConflicts.map((conflict) => (
                            <li key={conflict.id}>
                                Ticket #{conflict.id} · caisse {conflict.code} · {formatTime(conflict.createdAt)}
                                {(conflict.conflictDetails || []).map((detail) => (
                                    <span key={`${conflict.id}-${detail.articleId}`}>
                                        {" — "}
                                        {detail.name}: demandé {detail.requested}, dispo {detail.available}
                                    </span>
                                ))}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {recentInvoices.length > 0 && (
                <div className="recent-invoices" style={{ marginTop: 24 }}>
                    <h3>Derniers tickets</h3>
                    <p className="caisses-panel__sub">
                        Relance l&apos;impression sur l&apos;imprimante de la caisse (pas sur ce téléphone).
                    </p>
                    <ul>
                        {recentInvoices.map((invoice) => (
                            <li key={invoice.id}>
                                #{invoice.id} · {caisseLabelForCode(invoice.code)} · {formatTime(invoice.createdAt)}
                                {" "}
                                <button
                                    type="button"
                                    className="btn-ghost btn-xs"
                                    disabled={reprintingId === invoice.id}
                                    onClick={() => reprintOnCaisse(invoice)}
                                >
                                    {reprintingId === invoice.id ? "Envoi…" : "Réimprimer sur caisse"}
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );

    const articleFormBlock = (
        <form className="article-form" onSubmit={saveArticle}>
            <div className="form-field">
                <label>Catégorie</label>
                <select
                    value={articleForm.categoryId || selectedCategoryId || ""}
                    onChange={(e) => setArticleForm({ ...articleForm, categoryId: e.target.value })}
                >
                    {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                </select>
            </div>
            <div className="form-field">
                <label>Nom</label>
                <input
                    type="text"
                    placeholder="Ex: Coca 33cl"
                    value={articleForm.name}
                    onChange={(e) => setArticleForm({ ...articleForm, name: e.target.value })}
                    required
                />
            </div>
            <div className="form-field">
                <label>Image</label>
                <input
                    type="text"
                    placeholder="/assets/img/mon-article.jpg"
                    value={articleForm.image}
                    onChange={(e) => setArticleForm({ ...articleForm, image: e.target.value })}
                />
            </div>
            <div className="form-row">
                <div className="form-field">
                    <label>Prix (€)</label>
                    <input
                        type="number"
                        step="0.01"
                        min="0"
                        inputMode="decimal"
                        placeholder="3.90"
                        value={articleForm.price}
                        onChange={(e) => setArticleForm({ ...articleForm, price: e.target.value })}
                        required
                    />
                </div>
                <div className="form-field">
                    <label>Stock (quantité)</label>
                    <input
                        type="number"
                        min="0"
                        step="1"
                        inputMode="numeric"
                        placeholder="Vide = illimité"
                        value={articleForm.stock}
                        onChange={(e) => setArticleForm({ ...articleForm, stock: e.target.value })}
                    />
                </div>
            </div>
            <label className="toggle-switch">
                <input
                    type="checkbox"
                    checked={articleForm.active}
                    onChange={(e) => setArticleForm({ ...articleForm, active: e.target.checked })}
                />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                <span className="toggle-label">Article actif</span>
            </label>
            <div className="form-actions">
                <button type="submit" className="btn-primary">
                    {editingArticleId ? "Enregistrer" : "Créer l'article"}
                </button>
                <button type="button" className="btn-ghost" onClick={resetForm}>Annuler</button>
            </div>
        </form>
    );

    const renderStatsBlock = (block, title, subtitle = "") => (
        <section className="stats-section" key={title}>
            <div className="stats-section__head">
                <h3>{title}</h3>
                {subtitle && <span className="stats-section__sub">{subtitle}</span>}
            </div>

            <div className="stats-cards">
                <div className="stat-card">
                    <span>Commandes</span>
                    <strong>{block.totalOrders}</strong>
                </div>
                <div className="stat-card">
                    <span>Articles vendus</span>
                    <strong>{block.totalItems}</strong>
                </div>
                <div className="stat-card">
                    <span>Chiffre d&apos;affaires</span>
                    <strong>{block.totalRevenue.toFixed(2)} €</strong>
                </div>
                <div className="stat-card">
                    <span>Horaires</span>
                    <strong className="stat-card__small">
                        {formatTime(block.firstSale)} → {formatTime(block.lastSale)}
                    </strong>
                </div>
            </div>

            <section className="stats-block">
                <h4>Ventes par heure</h4>
                <div className="hour-chart">
                    {block.byHour.map((entry) => (
                        <div key={entry.hour} className="hour-chart__col" title={`${entry.hour}h : ${entry.orders} cmd, ${entry.items} art.`}>
                            <div
                                className="hour-chart__bar"
                                style={{ height: `${(entry.orders / block.maxHourlyOrders) * 100}%` }}
                            />
                            <span className="hour-chart__label">{entry.hour}h</span>
                            {entry.orders > 0 && <span className="hour-chart__val">{entry.orders}</span>}
                        </div>
                    ))}
                </div>
            </section>

            <section className="stats-block">
                <h4>Articles les plus vendus</h4>
                {block.topArticles.length === 0 ? (
                    <p className="stats-empty">Aucune vente pour cette période.</p>
                ) : (
                    <div className="top-articles">
                        {block.topArticles.map((article, i) => (
                            <div key={`${title}-${article.name}`} className="top-article">
                                <span className="top-article__rank">{i + 1}</span>
                                <span className="top-article__name">{article.name}</span>
                                <span className="top-article__qty">{article.quantity}</span>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </section>
    );

    const statsPanel = (
        <div className="stats-panel anim-fade">
            <div className="stats-panel__head">
                <h2>Statistiques de vente</h2>
                <div className="stats-scope">
                    <button
                        type="button"
                        className={statsScope === "today" ? "is-active" : ""}
                        onClick={() => setStatsScope("today")}
                    >
                        Aujourd&apos;hui
                    </button>
                    <button
                        type="button"
                        className={statsScope === "all" ? "is-active" : ""}
                        onClick={() => setStatsScope("all")}
                    >
                        Tout
                    </button>
                </div>
            </div>

            {statsLoading ? (
                <div className="skeleton-list">
                    {[1, 2, 3].map((n) => <div key={n} className="skeleton-row anim-shimmer" />)}
                </div>
            ) : stats && (
                <>
                    {renderStatsBlock(stats.global, "Total cumulé", "Les deux caisses")}
                    {stats.byCaisse?.length > 0 && (
                        <div className="stats-by-caisse">
                            <h3 className="stats-by-caisse__title">Par caisse</h3>
                            {stats.byCaisse.map((caisse) => renderStatsBlock(
                                caisse,
                                caisse.label,
                                `Code ${caisse.code}`,
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );

    const categoriesPanel = (
        <div className="categories-panel anim-fade">
            <h2>Gestion des catégories</h2>
            <div className="mob-cat-add mob-cat-add--full">
                <input
                    type="text"
                    placeholder="Nom de la catégorie"
                    value={categoryName}
                    onChange={(e) => setCategoryName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addCategory()}
                />
                <select value={categoryStand} onChange={(e) => setCategoryStand(e.target.value)}>
                    {STAND_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                </select>
                <button type="button" className="btn-primary" onClick={addCategory}>Ajouter</button>
            </div>
            <div className="mob-cat-list">
                {categories.map((cat, i) => (
                    <div key={cat.id} className={`mob-cat-item anim-stagger ${!cat.active ? "is-off" : ""}`} style={{ animationDelay: `${i * 60}ms` }}>
                        <div>
                            <strong>{cat.name}</strong>
                            <small>{cat.articles.length} articles · Ticket {STANDS[cat.stand || 'repas']?.shortLabel}</small>
                        </div>
                        <div className="mob-cat-item__actions">
                            <select
                                value={cat.stand || 'repas'}
                                onChange={(e) => updateCategoryStand(cat, e.target.value)}
                            >
                                {STAND_OPTIONS.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                            <button type="button" className="btn-ghost btn-xs" onClick={() => toggleCategory(cat)}>
                                {cat.active ? "Désactiver" : "Activer"}
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    const catalogTable = selectedCategory && (
        <>
            <div className="main-content__head">
                <h2>{selectedCategory.name}</h2>
                <div className="main-content__actions">
                    <button type="button" className="btn-primary" onClick={openNew}>+ Article</button>
                    <button type="button" className="btn-ghost" onClick={() => toggleCategory(selectedCategory)}>
                        {selectedCategory.active ? "Désactiver cat." : "Activer cat."}
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="skeleton-list">
                    {[1, 2, 3].map((n) => <div key={n} className="skeleton-row anim-shimmer" />)}
                </div>
            ) : (
                <div className="table-wrap">
                    <table className="article-table">
                        <thead>
                            <tr>
                                <th>Article</th>
                                <th>Prix</th>
                                <th>Stock</th>
                                <th>Statut</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {selectedCategory.articles.map((article, i) => (
                                <tr
                                    key={article.id}
                                    className={`anim-stagger ${!article.active ? "is-off" : ""}`}
                                    style={{ animationDelay: `${i * 30}ms` }}
                                >
                                    <td className="article-table__name">{article.name}</td>
                                    <td>{Number(article.price).toFixed(2)} €</td>
                                    <td>
                                        <span className={`stock-badge ${article.stock === null ? "stock-badge--∞" : article.stock <= 0 ? "stock-badge--empty" : ""}`}>
                                            {article.stock === null ? "∞" : article.stock}
                                        </span>
                                    </td>
                                    <td>
                                        <label className="toggle-switch toggle-switch--sm">
                                            <input type="checkbox" checked={article.active} onChange={() => toggleArticle(article)} />
                                            <span className="toggle-track"><span className="toggle-thumb" /></span>
                                        </label>
                                    </td>
                                    <td className="article-table__actions">
                                        <button type="button" className="btn-icon" onClick={() => openEdit(article)} title="Éditer">✏️</button>
                                        <button type="button" className="btn-icon btn-icon--danger" onClick={() => deleteArticle(article.id)} title="Supprimer">🗑</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </>
    );

    if (!token) {
        return (
            <div className="admin">
                <div className="admin-login-scene">
                    <div className="admin-login-bg" />
                    <form className="admin-login-card anim-rise" onSubmit={login}>
                        <div className="login-logo anim-pulse">🛒</div>
                        <h1>Caisse Foire</h1>
                        <p>Gestion du catalogue</p>
                        <div className="pin-field">
                            <input
                                type="password"
                                inputMode="numeric"
                                placeholder="Code PIN"
                                maxLength={8}
                                value={pin}
                                onChange={(e) => setPin(e.target.value)}
                                autoComplete="current-password"
                            />
                        </div>
                        <button type="submit" className="btn-primary btn-full">Entrer</button>
                        {message && <p className="toast toast--error anim-shake">{message}</p>}
                    </form>
                </div>
            </div>
        );
    }

    return (
        <div className="admin admin--app">
            <header className="topbar anim-slide-down">
                <div className="topbar__brand">
                    <span className="topbar__icon">🛒</span>
                    <div>
                        <strong>Caisse Foire</strong>
                        <small>{totalArticles} articles · {categories.length} catég.</small>
                    </div>
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <button
                        type="button"
                        className={`btn-ghost btn-sm ${pushEnabled ? "is-active" : ""}`}
                        onClick={togglePush}
                        disabled={pushBusy}
                        title="Alertes stock, caisses hors ligne…"
                    >
                        {pushBusy ? "…" : (pushEnabled ? "Notifs ON" : "Activer notifs")}
                    </button>
                    <button type="button" className="btn-ghost btn-sm" onClick={logout}>Quitter</button>
                </div>
            </header>
            {pushHint && (
                <p className="toast anim-slide-up" style={{ margin: "8px 12px 0" }}>{pushHint}</p>
            )}

            <nav className="desktop-nav">
                <button type="button" className={desktopView === VIEWS.CATALOG ? "is-active" : ""} onClick={() => setDesktopView(VIEWS.CATALOG)}>Catalogue</button>
                <button type="button" className={desktopView === VIEWS.STATS ? "is-active" : ""} onClick={() => setDesktopView(VIEWS.STATS)}>Statistiques</button>
                <button type="button" className={desktopView === VIEWS.CAISSES ? "is-active" : ""} onClick={() => setDesktopView(VIEWS.CAISSES)}>
                    Caisses{onlineCount > 0 ? ` (${onlineCount})` : ""}
                </button>
                <button type="button" className={desktopView === VIEWS.CATEGORIES ? "is-active" : ""} onClick={() => setDesktopView(VIEWS.CATEGORIES)}>Catégories</button>
            </nav>

            <div className={`admin-desktop ${desktopView !== VIEWS.CATALOG ? "admin-desktop--wide" : ""}`}>
                <aside className="sidebar anim-fade">
                    <h3>Catégories</h3>
                    <ul className="sidebar__list">
                        {categories.map((cat, i) => (
                            <li key={cat.id} style={{ animationDelay: `${i * 40}ms` }} className="anim-stagger">
                                <button
                                    type="button"
                                    className={`sidebar__item ${selectedCategoryId === cat.id ? "is-active" : ""} ${!cat.active ? "is-off" : ""}`}
                                    onClick={() => { setSelectedCategoryId(cat.id); setDesktopView(VIEWS.CATALOG); }}
                                >
                                    <span className="sidebar__label">
                                        <span>{cat.name}</span>
                                        <small>{STANDS[cat.stand || 'repas']?.shortLabel}</small>
                                    </span>
                                    <span className="sidebar__count">{cat.articles.length}</span>
                                </button>
                            </li>
                        ))}
                    </ul>
                </aside>

                <main className="main-content">
                    {desktopView === VIEWS.CATALOG && catalogTable}
                    {desktopView === VIEWS.STATS && statsPanel}
                    {desktopView === VIEWS.CAISSES && caissesPanel}
                    {desktopView === VIEWS.CATEGORIES && categoriesPanel}
                </main>
            </div>

            <div className="admin-mobile">
                {mobileView === VIEWS.CATALOG && (
                    <div className="mob-catalog anim-fade">
                        <div className="mob-chips">
                            {categories.map((cat) => (
                                <button
                                    type="button"
                                    key={cat.id}
                                    className={`mob-chip ${selectedCategoryId === cat.id ? "is-active" : ""} ${!cat.active ? "is-off" : ""}`}
                                    onClick={() => setSelectedCategoryId(cat.id)}
                                >
                                    {cat.name}
                                    <span className="mob-chip__n">{cat.articles.length}</span>
                                </button>
                            ))}
                        </div>
                        {selectedCategory && (
                            <div className="mob-section anim-rise">
                                <div className="mob-section__head">
                                    <h2>{selectedCategory.name}</h2>
                                    <button type="button" className="btn-ghost btn-xs" onClick={() => toggleCategory(selectedCategory)}>
                                        {selectedCategory.active ? "Off" : "On"}
                                    </button>
                                </div>
                                {loading ? (
                                    <div className="skeleton-list">
                                        {[1, 2, 3, 4].map((n) => <div key={n} className="skeleton-card anim-shimmer" />)}
                                    </div>
                                ) : (
                                    <div className="mob-cards">
                                        {selectedCategory.articles.map((article, i) => (
                                            <div key={article.id} className={`mob-card anim-stagger ${!article.active ? "is-off" : ""}`} style={{ animationDelay: `${i * 50}ms` }}>
                                                <div className="mob-card__info">
                                                    <strong>{article.name}</strong>
                                                    <div className="mob-card__meta">
                                                        <span className="mob-card__price">{Number(article.price).toFixed(2)} €</span>
                                                        <span className={`stock-badge ${article.stock === null ? "stock-badge--∞" : article.stock <= 0 ? "stock-badge--empty" : ""}`}>
                                                            {article.stock === null ? "∞" : `${article.stock} rest.`}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="mob-card__actions">
                                                    <label className="toggle-switch toggle-switch--sm">
                                                        <input type="checkbox" checked={article.active} onChange={() => toggleArticle(article)} />
                                                        <span className="toggle-track"><span className="toggle-thumb" /></span>
                                                    </label>
                                                    <button type="button" className="btn-icon" onClick={() => openEdit(article)}>✏️</button>
                                                    <button type="button" className="btn-icon btn-icon--danger" onClick={() => deleteArticle(article.id)}>🗑</button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {mobileView === VIEWS.FORM && (
                    <div className="mob-form-view anim-rise">
                        <h2>{editingArticleId ? "Modifier l'article" : "Nouvel article"}</h2>
                        {articleFormBlock}
                    </div>
                )}

                {mobileView === VIEWS.CATEGORIES && categoriesPanel}
                {mobileView === VIEWS.STATS && statsPanel}
                {mobileView === VIEWS.CAISSES && caissesPanel}

                {mobileView === VIEWS.CATALOG && (
                    <button type="button" className="fab anim-bounce" onClick={openNew} aria-label="Ajouter article">+</button>
                )}

                <nav className="bottom-nav anim-slide-up">
                    <button type="button" className={`bottom-nav__item ${mobileView === VIEWS.CATALOG ? "is-active" : ""}`} onClick={() => setMobileView(VIEWS.CATALOG)}>
                        <span className="bottom-nav__icon">📋</span>
                        <span>Catalogue</span>
                    </button>
                    <button type="button" className={`bottom-nav__item ${mobileView === VIEWS.STATS ? "is-active" : ""}`} onClick={() => setMobileView(VIEWS.STATS)}>
                        <span className="bottom-nav__icon">📊</span>
                        <span>Stats</span>
                    </button>
                    <button type="button" className={`bottom-nav__item ${mobileView === VIEWS.CAISSES ? "is-active" : ""}`} onClick={() => setMobileView(VIEWS.CAISSES)}>
                        <span className="bottom-nav__icon">🖥</span>
                        <span>Caisses</span>
                    </button>
                    <button type="button" className={`bottom-nav__item ${mobileView === VIEWS.CATEGORIES ? "is-active" : ""}`} onClick={() => setMobileView(VIEWS.CATEGORIES)}>
                        <span className="bottom-nav__icon">📁</span>
                        <span>Catég.</span>
                    </button>
                </nav>
            </div>

            {formOpen && (
                <div className="form-drawer-overlay anim-fade" onClick={resetForm}>
                    <aside className="form-drawer anim-slide-left" onClick={(e) => e.stopPropagation()}>
                        <div className="form-drawer__head">
                            <h3>{editingArticleId ? "Modifier l'article" : "Nouvel article"}</h3>
                            <button type="button" className="close-btn" onClick={resetForm}>×</button>
                        </div>
                        {articleFormBlock}
                    </aside>
                </div>
            )}

            {incomingAlert && (
                <div className="alert-call-overlay" role="dialog" aria-modal="true" aria-label="Appel urgence caisse">
                    <div className="alert-call">
                        <div className="alert-call__pulse" />
                        <p className="alert-call__label">Appel urgence</p>
                        <h2 className="alert-call__title">
                            {incomingAlert.caisseName || `Caisse ${incomingAlert.code}`}
                        </h2>
                        <p className="alert-call__msg">{incomingAlert.message}</p>
                        <button
                            type="button"
                            className="alert-call__ack"
                            onClick={ackIncomingAlert}
                            disabled={ackBusy}
                        >
                            {ackBusy ? "…" : "Valider — j’ai vu"}
                        </button>
                        <p className="alert-call__hint">La sonnerie continue tant que tu n’as pas validé</p>
                    </div>
                </div>
            )}

            {message && <div className="toast anim-slide-up">{message}</div>}
        </div>
    );
};

export default AdminPanel;
